/**
 * פקודת משימה — תצוגה לאורך: the portrait dispatch order of a single task.
 *
 * The wide dispatch sheet (`src/core/dispatch-report.ts`) is a printer's
 * document — six columns that a phone can only read sideways. A driver reads
 * the order on a phone, so the same facts are restated here as one vertical
 * card: the six blocks a written dispatch order carries, one under the other,
 * nothing running off the edge.
 *
 * There is no second calculator behind it. The card is built from a
 * {@link DispatchRow} the report engine already produced, so a task reads the
 * same on paper, on the board and in a WhatsApp message. What this module adds
 * is the *order* of the blocks, the WhatsApp text and the file name — and
 * nothing here touches the DOM, so the healthcheck builds the very same card
 * without a browser.
 */
import {
  EMPTY_DISPATCH_RANGE,
  buildDispatchReport,
  crewIdentityLine,
} from './dispatch-report';
import type { DispatchCrewLine, DispatchInput, DispatchRow, DispatchTeamLine } from './dispatch-report';
import { describeTemporaryVehicle } from './external-people';
import { EXTERNAL_PERSON_TAG } from '../types/assignment';
import { googleMapsHref, telHref, whatsappTextHref } from './field-links';
import { formatPhone } from './phone';
import type { Task } from '../types/task';

// ── The blocks the order is read in ──────────────────────────────────

/** The title the screens and the image both carry. */
export const DISPATCH_CARD_TITLE = 'פקודת משימה - תצוגה לאורך';

/** The six blocks of a written dispatch order, in reading order. */
export const DISPATCH_CARD_SECTIONS = [
  'headline',
  'times',
  'destination',
  'vehicles',
  'crew',
  'notes',
] as const;

export type DispatchCardSection = (typeof DISPATCH_CARD_SECTIONS)[number];

export const DISPATCH_CARD_SECTION_TITLES: Record<DispatchCardSection, string> = {
  headline: 'כותרת המשימה וסוגה',
  times: 'זמנים ומועד ביצוע',
  destination: 'יעד, קו ואיש קשר',
  vehicles: 'רכבים משובצים',
  crew: 'צוות ונהגים',
  notes: 'הערות ודגשים מבצעיים',
};

// ── The card ─────────────────────────────────────────────────────────

/** One vehicle of the convoy, as the order names it. */
export interface DispatchCardVehicle {
  teamId: string;
  /** The three-digit internal number — printed largest on the card. */
  callSign: string;
  name: string;
  model: string;
  plateNumber: string;
  typeLabel: string;
  company: string;
  crewCount: number;
  /**
   * The people riding *this* vehicle, driver first — and only the roles that
   * were actually assigned. A vehicle with no task commander carries no
   * commander line at all; the order never prints "מפקד משימה: —".
   */
  crew: DispatchCardCrew[];
  note: string;
}

/** One person of the convoy, with the vehicle they ride on. */
export interface DispatchCardCrew {
  teamId: string;
  /** The vehicle's call sign, so a name is never loose on the order. */
  vehicleCallSign: string;
  roleLabel: string;
  name: string;
  /**
   * The soldier's מספר אישי — printed beside the name on every reading of the
   * order. `''` for a rider the unit holds no personal number for.
   */
  personalNumber: string;
  jobLabel: string;
  phone: string;
  /** `tel:` href for the quick-dial button, or `''` when no number is known. */
  phoneHref: string;
  licenseLabel: string;
  /** True for somebody off the roster — marked "איש חוץ" on every reading. */
  external: boolean;
  /**
   * The vehicle an external person arrived in, already spelled as the order
   * prints it: `רכב זמני: פורד טרנזיט · 12-345-67`, or `''` when they brought
   * none. The plate is kept beside it because the image draws it as a tag of
   * its own rather than as part of the sentence.
   */
  temporaryVehicle: string;
  temporaryVehicleModel: string;
  temporaryPlateNumber: string;
}

export interface DispatchCard {
  taskId: string;
  title: string;
  typeName: string;
  statusLabel: string;
  dateKey: string;
  dateLabel: string;
  startTime: string;
  endTime: string;
  /** `08:00–12:00`, or the start alone when no estimate was given. */
  window: string;
  destination: string;
  /** The route line — the manager's own link, else a maps route to the address. */
  routeHref: string;
  contactName: string;
  contactRole: string;
  contactPhone: string;
  contactPhoneHref: string;
  vehicles: DispatchCardVehicle[];
  crew: DispatchCardCrew[];
  notes: string;
  vehicleCount: number;
  crewCount: number;
}

const text = (value: string | null | undefined): string => String(value ?? '').trim();

/** What a block shows when the manager has not filled it in yet. */
export const DISPATCH_CARD_EMPTY = '—';

/** `— · ` never reaches the card: an empty part is dropped, not printed. */
export const joinParts = (parts: readonly (string | null | undefined)[], separator = ' · '): string =>
  parts.map(text).filter(Boolean).join(separator);

function crewOf(team: DispatchTeamLine, line: DispatchCrewLine | null): DispatchCardCrew | null {
  if (!line) return null;
  const phone = text(line.phone);
  return {
    teamId: team.teamId,
    vehicleCallSign: team.vehicleMilitaryId,
    roleLabel: line.roleLabel,
    name: text(line.name) || DISPATCH_CARD_EMPTY,
    personalNumber: text(line.personalNumber),
    jobLabel: text(line.jobLabel),
    phone: phone ? formatPhone(phone) : '',
    phoneHref: telHref(phone) ?? '',
    licenseLabel: text(line.licenseLabel),
    external: line.external,
    temporaryVehicle: describeTemporaryVehicle(line),
    temporaryVehicleModel: text(line.vehicleModel),
    temporaryPlateNumber: text(line.plateNumber),
  };
}

/**
 * The riders of one vehicle, driver first — and only the roles that were
 * filled. An unassigned seat produces no line, so the order shows a driver
 * alone, a driver and an escort, or a driver, an escort and riders, and never
 * an empty row with a dash in it.
 */
function vehicleCrew(team: DispatchTeamLine): DispatchCardCrew[] {
  const rows: DispatchCardCrew[] = [];
  for (const line of [team.driver, team.escort, ...team.passengers]) {
    const row = crewOf(team, line);
    if (row) rows.push(row);
  }
  return rows;
}

/** The portrait order of one task, from the row the report engine built. */
export function buildDispatchCard(row: DispatchRow): DispatchCard {
  const [startTime, endTime] = row.window.split('–');
  const vehicles: DispatchCardVehicle[] = row.teams.map((team) => ({
    teamId: team.teamId,
    callSign: team.vehicleMilitaryId,
    name: team.vehicleName,
    model: team.vehicleModel,
    plateNumber: team.plateNumber,
    typeLabel: team.vehicleTypeLabel,
    company: team.vehicleCompany,
    crewCount: team.crewCount,
    crew: vehicleCrew(team),
    note: text(team.teamNote),
  }));
  return {
    taskId: row.taskId,
    title: text(row.title) || DISPATCH_CARD_EMPTY,
    typeName: text(row.typeName),
    statusLabel: row.statusLabel,
    dateKey: row.date,
    dateLabel: row.dateLabel,
    startTime: text(startTime) || text(row.startTime),
    endTime: text(endTime),
    window: row.window,
    destination: row.destination,
    routeHref: row.destinationLink || googleMapsHref(row.destination) || '',
    contactName: row.contactName,
    contactRole: row.contactRole,
    contactPhone: row.contactPhone ? formatPhone(row.contactPhone) : '',
    contactPhoneHref: telHref(row.contactPhone) ?? '',
    vehicles,
    crew: vehicles.flatMap((vehicle) => vehicle.crew),
    notes: row.operationalNotes,
    vehicleCount: row.vehicleCount,
    crewCount: row.crewCount,
  };
}

/**
 * The order of one task, built through the report engine so the card, the sheet
 * and the printed page can never drift apart. A cancelled task still has an
 * order — a driver holding one is exactly who needs to be told it is off.
 */
export function buildTaskDispatchCard(
  task: Task,
  input: Omit<DispatchInput, 'tasks' | 'range' | 'includeCancelled'> = {},
): DispatchCard | null {
  const report = buildDispatchReport({
    ...input,
    tasks: [task],
    range: EMPTY_DISPATCH_RANGE,
    includeCancelled: true,
  });
  const [row] = report.rows;
  return row ? buildDispatchCard(row) : null;
}

// ── Paging a convoy ──────────────────────────────────────────────────

/**
 * How many vehicles one shared image may carry.
 *
 * A phone screen is the whole constraint: past three vehicles the type has to
 * shrink to fit, and an order a driver has to pinch to read is not an order. A
 * long convoy is therefore split into several images rather than one tall one.
 */
export const DISPATCH_PAGE_VEHICLES = 3;

/** One image of a — possibly multi-part — order. */
export interface DispatchCardPage {
  /** 1-based, as the header prints it. */
  pageNumber: number;
  pageCount: number;
  /** `פקודת משימה — חלק 2 מתוך 3`, or `''` when the order is one image. */
  pageLabel: string;
  /** Only the first page carries the times, the destination and the notes. */
  detailed: boolean;
  /** Where this page's first vehicle sits in the whole convoy, 1-based. */
  firstVehicle: number;
  /** The order itself, with `vehicles` and `crew` narrowed to this page. */
  card: DispatchCard;
}

/** The header line of part `n` — empty for an order that fits one image. */
export function dispatchPageLabel(pageNumber: number, pageCount: number): string {
  return pageCount > 1 ? `פקודת משימה — חלק ${pageNumber} מתוך ${pageCount}` : '';
}

/**
 * Splits an order into the images it will be shared as.
 *
 * One to three vehicles produce a single page; four or more are paginated, the
 * first page carrying the mission details and every later page a compact
 * header and the next slice of the convoy. `vehicleCount` and `crewCount` stay
 * the totals of the whole mission on every page, so part 2 can still say how
 * large the convoy it belongs to is.
 */
export function paginateDispatchCard(
  card: DispatchCard,
  perPage: number = DISPATCH_PAGE_VEHICLES,
): DispatchCardPage[] {
  const size = Math.max(1, Math.floor(perPage) || DISPATCH_PAGE_VEHICLES);
  const pageCount = Math.max(1, Math.ceil(card.vehicles.length / size));
  const pages: DispatchCardPage[] = [];
  for (let index = 0; index < pageCount; index += 1) {
    const vehicles = card.vehicles.slice(index * size, index * size + size);
    pages.push({
      pageNumber: index + 1,
      pageCount,
      pageLabel: dispatchPageLabel(index + 1, pageCount),
      detailed: index === 0,
      firstVehicle: index * size + 1,
      card: { ...card, vehicles, crew: vehicles.flatMap((vehicle) => vehicle.crew) },
    });
  }
  return pages;
}

/** The whole order as one page — what a preview and a short convoy both want. */
export function singleDispatchPage(card: DispatchCard): DispatchCardPage {
  return {
    pageNumber: 1,
    pageCount: 1,
    pageLabel: '',
    detailed: true,
    firstVehicle: 1,
    card,
  };
}

// ── The WhatsApp message ─────────────────────────────────────────────

/** The line a shared order signs off with. */
export const DISPATCH_CARD_FOOTER = 'הופק ממערכת משימות שינוע 99';

/**
 * The order as formatted WhatsApp text — the fallback for a browser that
 * cannot share a file, and a useful message in its own right.
 *
 * Plain type throughout: no emoji and no urgency tag, the same operational
 * register the image is drawn in. Every line is conditional, so a task with no
 * contact, no convoy and no notes produces a short message rather than a
 * message full of dashes, and nothing ever leaks a `null` into the field.
 */
export function dispatchCardText(card: DispatchCard): string {
  const lines: string[] = [];

  lines.push(`*${card.title}*`);
  lines.push(joinParts([card.typeName, card.statusLabel], ' | '));
  lines.push('');

  lines.push(DISPATCH_CARD_SECTION_TITLES.times);
  lines.push(joinParts([`תאריך: ${card.dateLabel}`, card.window ? `שעות: ${card.window}` : ''], ' | '));

  if (card.destination || card.contactName || card.routeHref) {
    lines.push('');
    lines.push(DISPATCH_CARD_SECTION_TITLES.destination);
    if (card.destination) lines.push(`יעד: ${card.destination}`);
    if (card.routeHref) lines.push(`קו ניווט: ${card.routeHref}`);
    if (card.contactName) {
      lines.push(joinParts([`איש קשר: ${card.contactName}`, card.contactRole, card.contactPhone]));
    }
  }

  if (card.vehicles.length > 0) {
    lines.push('');
    lines.push(`${DISPATCH_CARD_SECTION_TITLES.vehicles} (${card.vehicles.length})`);
    for (const vehicle of card.vehicles) {
      lines.push('');
      lines.push(
        `*${vehicle.callSign}* ${joinParts([
          vehicle.model || vehicle.name,
          vehicle.plateNumber,
          vehicle.typeLabel,
          vehicle.company,
        ])}`,
      );
      // Only the seats that were filled — an unassigned escort has no line.
      for (const member of vehicle.crew) {
        lines.push(
          joinParts([
            // תפקיד: שם מלא (מ.א. … | טלפון) — the one spelling of a crew line.
            crewIdentityLine(member.roleLabel, member),
            member.jobLabel,
            member.licenseLabel ? `רישיון ${member.licenseLabel}` : '',
            // The mark reads as the unit's own word, and it is the *last* part
            // of the line so the name still leads it.
            member.external ? EXTERNAL_PERSON_TAG : '',
          ]),
        );
        // The vehicle they arrived in gets a line of its own: it is a fact about
        // the convoy, not a footnote on a name.
        if (member.temporaryVehicle) lines.push(member.temporaryVehicle);
      }
      if (vehicle.note) lines.push(`הערת צוות: ${vehicle.note}`);
    }
  }

  if (card.notes) {
    lines.push('');
    lines.push(DISPATCH_CARD_SECTION_TITLES.notes);
    lines.push(card.notes);
  }

  lines.push('');
  lines.push(DISPATCH_CARD_FOOTER);

  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// ── The caption the image travels with ───────────────────────────────

/** The word the caption's bold headline opens with. */
export const DISPATCH_CAPTION_TITLE_PREFIX = 'משימה';

/** The heading of the contact block — printed only when there is a contact. */
export const DISPATCH_CAPTION_CONTACT_TITLE = 'איש קשר למשימה:';

/**
 * The short text WhatsApp shows under the shared image.
 *
 * The image already carries the whole order; a caption that repeated it would
 * be a second, drifting copy of the same facts. So it says only the two things
 * a chat needs read at a glance: which mission this is, and who to call in the
 * field. A task with no contact defined sends the bold title alone — no
 * heading over an empty block, no dash standing in for a phone number.
 */
export function dispatchCardCaption(card: DispatchCard): string {
  const lines: string[] = [
    `*${DISPATCH_CAPTION_TITLE_PREFIX}: ${text(card.title) || DISPATCH_CARD_EMPTY}*`,
  ];
  const name = text(card.contactName);
  const phone = text(card.contactPhone);
  if (name || phone) {
    lines.push('');
    lines.push(DISPATCH_CAPTION_CONTACT_TITLE);
    if (name) lines.push(`שם: ${name}`);
    if (phone) lines.push(`טלפון: ${phone}`);
  }
  return lines.join('\n');
}

/**
 * The recipient-less WhatsApp link the text fallback opens, carrying the whole
 * order as its message. How that link is spelled is `whatsappTextHref()`'s
 * business alone — see `src/core/field-links.ts`.
 */
export function dispatchCardShareHref(card: DispatchCard): string {
  return whatsappTextHref(dispatchCardText(card)) ?? 'https://wa.me/';
}

/**
 * The name the image is downloaded and shared under. ASCII on purpose — a
 * Hebrew file name survives neither every share target nor every file system.
 *
 * A paginated convoy numbers its parts (`…-part2of3.png`) so the images stay in
 * order in the chat, in the download folder and in the share sheet alike.
 */
export function dispatchCardFileName(
  card: DispatchCard,
  page?: Pick<DispatchCardPage, 'pageNumber' | 'pageCount'>,
): string {
  const day = card.dateKey || 'task';
  const id = card.taskId.replace(/[^A-Za-z0-9-]/g, '').slice(-8) || 'order';
  const part = page && page.pageCount > 1 ? `-part${page.pageNumber}of${page.pageCount}` : '';
  return `dispatch-order-${day}-${id}${part}.png`;
}
